import { Box, Button, Container, Heading, Text, VStack, useToast, Flex, Collapse, useDisclosure, Link } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useState } from 'react';
import { useCourseConfig } from '../../../hooks/useCourseConfig';

export default function ND101ACON1ALanding() {
  const navigate = useNavigate();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const courseConfig = useCourseConfig();
  const { isOpen: isOpenWhy, onToggle: onToggleWhy } = useDisclosure();
  const { isOpen: isOpenHow, onToggle: onToggleHow } = useDisclosure();

  const handleConsent = async () => {
    setIsLoading(true);
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data: courseData } = await supabase
        .from('courses')
        .select('id')
        .eq('code', courseConfig.code)
        .single();

      if (!courseData) {
        throw new Error('Course not found');
      }

      const { error } = await supabase
        .from('course_learner_preferences')
        .upsert(
          { 
            user_id: user.id, 
            course_id: courseData.id,
            consent_given: true,
          },
          { 
            onConflict: 'user_id,course_id',
            ignoreDuplicates: false
          }
        );

      if (error) throw error;

      toast({
        title: 'Consent recorded',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      navigate('/preferences');
    } catch (error) {
      console.error('Error recording consent:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      toast({
        title: 'Error recording consent',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box bg="white" minH="100vh" py={12}>
      <Container maxW="4xl">
        <VStack spacing={6} textAlign="center" mb={8}>
          <Heading as="h1" size="2xl" color="gray.800">
            Rad Learning Tutor
          </Heading>
          <Text fontSize="xl" color="gray.600" maxW="2xl">
            Complete your Neurodiversity Learning Experience
          </Text>
        </VStack>

        <Box 
          bg="white" 
          p={8} 
          borderRadius="xl" 
          mb={12}
          borderWidth="2px" 
          borderColor="#1AC8ED"
          boxShadow="sm"
        >
          <Heading as="h2" size="lg" color="#C200FB" mb={6}>
            Here's what happens in this activity
          </Heading>
          <Box as="ol" listStyleType="none" mb={6}>
            {[
              <Text key="consent"><Text as="span" fontWeight="bold">Consent</Text> to having your responses recorded</Text>,
              <Text key="preferences"><Text as="span" fontWeight="bold">Set</Text> your communication preferences (or keep them the same)</Text>,
              <Text key="reflect"><Text as="span" fontWeight="bold">Reflect</Text> on what came up for you since the workshop</Text>,
              <Text key="chat">Chat with the AI Tutor to <Text as="span" fontWeight="bold">explore</Text> and <Text as="span" fontWeight="bold">apply</Text> what you've learned</Text>,
              <Text key="goal">Create a <Text as="span" fontWeight="bold">goal</Text> for your workplace as you exit</Text>
            ].map((item, index) => (
              <Flex as="li" key={index} mb={3}>
                <Flex align="flex-start">
                  <Flex
                    align="center"
                    justify="center"
                    bg="#C200FB"
                    color="white"
                    fontWeight="bold"
                    borderRadius="full"
                    w={8}
                    h={8}
                    flexShrink={0}
                    mr={3}
                    aria-hidden="true"
                  >
                    {index + 1}
                  </Flex>
                  <Box flex="1" mt={1} lineHeight="tall">{item}</Box>
                </Flex>
              </Flex>
            ))}
          </Box>
        </Box>

        <Box 
          bg="white"
          p={8}
          borderRadius="xl"
          borderWidth="2px"
          borderColor="#1AC8ED"
          boxShadow="sm"
          mb={12}
        >
          <Heading as="h2" size="lg" color="#C200FB" mb={6}>
            Here's why this is important
          </Heading>
          <Text color="gray.700" lineHeight="tall" mb={6}>
            The week since the workshop gave you time to think. Now this activity helps you turn those thoughts into action. You'll chat with the Tutor about what matters most to you, then leave with one concrete goal for your workplace. This is the difference between "that was an interesting workshop" and "here's what I'm actually going to do."
          </Text>

          <Button
            onClick={onToggleWhy}
            variant="link"
            color="#C200FB"
            fontWeight="semibold"
            mb={4}
            _hover={{ textDecoration: 'underline' }}
          >
            {isOpenWhy ? '▼' : '▶'} Curious to learn more?
          </Button>

          <Collapse in={isOpenWhy} animateOpacity>
            <Box p={4} bg="gray.50" borderRadius="md" mb={6}>
              <Heading as="h4" size="sm" color="gray.800" mb={3}>
                The workshop was collaborative. This is personal.
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={4}>
                In the workshop, you heard lots of voices and ideas. Now you get space to connect the learning to your work, your circumstances, your challenges. The Tutor helps you figure out what's most relevant for you.
              </Text>

              <Heading as="h4" size="sm" color="gray.800" mb={3}>
                Ideas need this step to become action.
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={4}>
                In a previous cohort, a team had a great idea during the workshop: create a sensory room for both staff and clients. But they didn't act on it right away - everyone was busy. When they completed the post-workshop activity, it brought the idea back to the surface. They reconnected, made a plan, and actually implemented it.
              </Text>
              <Text color="gray.700" lineHeight="tall" mb={4}>
                Without this step, good ideas fade into busy weeks. With it, they become real change.
              </Text>

              <Heading as="h4" size="sm" color="gray.800" mb={3}>
                Your responses stay private.
              </Heading>
              <Text color="gray.700" lineHeight="tall">
                We use your conversation to understand how the cohort is learning and to improve future experiences. We never share individual responses with your employer - only cohort-level insights about what the group learned and what might help next. We'll send you your personal goal so you don't forget it.
              </Text>
            </Box>
          </Collapse>
        </Box>

        <Box 
          bg="white" 
          p={8} 
          borderRadius="xl" 
          borderWidth="2px"
          borderColor="#C200FB"
          boxShadow="sm"
          mb={12}
        >
          <Heading as="h2" size="lg" color="#C200FB" mb={6}>
            Consent to having your interactions recorded
          </Heading>
          
          <Heading as="h3" size="md" color="gray.800" mb={4}>
            How does the Rad Learning Tutor work?
          </Heading>
          <Text color="gray.700" lineHeight="tall" mb={6}>
            The Rad Learning Tutor has the content from your workshop and context from your organisation. It will ask you questions to help you reflect on what you've learned and apply it to your workplace. Before you begin, it will ask how you like to communicate and learn, so it can adapt to what works best for you.
          </Text>

          <Button
            onClick={onToggleHow}
            variant="link"
            color="#C200FB"
            fontWeight="semibold"
            mb={4}
            _hover={{ textDecoration: 'underline' }}
          >
            {isOpenHow ? '▼' : '▶'} Curious to learn more?
          </Button>

          <Collapse in={isOpenHow} animateOpacity>
            <Box p={4} bg="gray.50" borderRadius="md" mb={6}>
              <Heading as="h4" size="sm" color="gray.800" mb={3}>
                What is it?
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={4}>
                The Rad Learning Tutor is an AI-powered chat application that helps you embed your learning by asking questions. This gives you an opportunity to explore what you know and what you're still learning. Even though it has access to all the learning materials, it's not a question-answering chatbot. Instead, it's a question-asking chatbot.
              </Text>

              <Heading as="h4" size="sm" color="gray.800" mb={3}>
                How does it work?
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={4}>
                The Tutor has access to the information you're learning and uses generative AI technology to create questions and respond to your comments. However, generative AI can sometimes be mistaken, so it's always important to ask a Rad Learning educator if anything is concerning or doesn't make sense.
              </Text>

              <Heading as="h4" size="sm" color="gray.800" mb={3}>
                Why is it important?
              </Heading>
              <Text color="gray.700" lineHeight="tall" mb={4}>
                The chat gives you space to reflect on and apply what you've learned. You can do it when it works for you and in the way you communicate best. It helps you turn workshop ideas into concrete actions for your workplace.
              </Text>
              <Text color="gray.700" lineHeight="tall">
                Your conversation also helps us understand what your cohort learned and what support might be helpful next. We only share cohort-level insights with your organisation, never individual responses.
              </Text>
            </Box>
          </Collapse>

          <Box borderTop="1px" borderColor="gray.200" pt={6} mt={6}>
            <Heading as="h3" size="md" color="gray.800" mb={4}>
              Our tutor's a great listener!
            </Heading>
            <Text color="gray.700" lineHeight="tall" mb={4}>
              By continuing, you agree that Rad Learning can record your responses.
            </Text>

            <Text color="gray.700" width="100%" textAlign="left" px={0} pl={4}>
                <Text as="span" fontWeight="bold">Your privacy:</Text><br />
                • Your data stays with Rad Learning and isn't used to train AI.<br />
                • We use your responses to improve our work and report on what the cohort learned - never on individuals.<br />
                • You can open our <Link href="https://www.radlearning.com.au/privacy-policy" isExternal color="#C200FB" textDecoration="underline">privacy statement</Link> if you want to read more.
              </Text>
            
            <Button 
              size="lg" 
              onClick={handleConsent}
              px={{ base: 4, md: 8 }}
              width={{ base: '100%', md: 'auto' }}
              height="auto"
              py={3}
              whiteSpace="normal"
              isLoading={isLoading}
            >
              I consent to recording my responses
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
